import { amber, blue, red } from '@mui/material/colors'
import { focusOutlines } from '@editor/hooks/useFocusElements/utils'
import {
  type FindingSeverity,
  type OpenFindingMarker,
  type SceneFinding,
  sameFinding,
} from 'src/state/stores/aiChatFindings'

export interface MarkerPoint {
  x: number
  y: number
  z: number
}

/** One "!" in the scene: the findings of the elements on this spot. */
export interface MarkerGroup {
  key: string
  position: MarkerPoint
  elementGuids: string[]
  findings: SceneFinding[]
  /** The worst severity of its findings: the colour of the "!". */
  severity: FindingSeverity
}

/** The colours of the findings' severities: errors red, warnings amber, hints blue. */
export const SEVERITY_COLORS: Record<FindingSeverity, string> = {
  error: red[600],
  warning: amber[700],
  info: blue[600],
}

export const SEVERITY_LABELS: Record<FindingSeverity, string> = {
  error: 'Fehler',
  warning: 'Warnung',
  info: 'Hinweis',
}

const SEVERITY_ORDER: FindingSeverity[] = ['error', 'warning', 'info']

/** "1 Befund", "3 Befunde". */
export const findingsTitle = (count: number): string =>
  count === 1 ? '1 Befund' : `${count} Befunde`

// Markers closer than this (1 cm) share one "!".
const GROUPING_PRECISION = 0.01

/**
 * Where the "!" of an element sits: the centre of its bounding box (its shape, or its axis for
 * bar elements), or null for an element the model does not have.
 */
export const markerPosition = (model: PlanarModel, elementGuid: string): MarkerPoint | null => {
  const points = focusOutlines(model, [elementGuid]).flat()
  if (points.length === 0) return null
  const xs = points.map(point => point.x)
  const ys = points.map(point => point.y)
  const zs = points.map(point => point.z)
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
    z: (Math.min(...zs) + Math.max(...zs)) / 2,
  }
}

const positionKey = ({ x, y, z }: MarkerPoint): string =>
  [x, y, z].map(value => Math.round(value / GROUPING_PRECISION)).join(':')

/**
 * The markers of the findings: one per element, elements on the same spot (a lintel in the
 * middle of its wall) share one. A finding of several elements is listed at each of them, once
 * per marker (identical findings once); the findings are sorted errors first. Elements the scene hides or the model does
 * not have get none.
 */
export const markerGroups = (
  model: PlanarModel,
  findings: SceneFinding[],
  hiddenElements: Set<string> = new Set(),
): MarkerGroup[] => {
  const groups = new Map<string, MarkerGroup>()
  const positions = new Map<string, MarkerPoint | null>()
  findings.forEach(finding => {
    finding.elementGuids.forEach(guid => {
      if (hiddenElements.has(guid)) return
      if (!positions.has(guid)) positions.set(guid, markerPosition(model, guid))
      const position = positions.get(guid)
      if (!position) return
      const key = positionKey(position)
      const group = groups.get(key) ?? {
        key,
        position,
        elementGuids: [],
        findings: [],
        severity: finding.severity,
      }
      if (!group.elementGuids.includes(guid)) group.elementGuids.push(guid)
      // Once per marker, also when the agent passed the same finding twice.
      if (!group.findings.some(other => sameFinding(other, finding))) {
        group.findings.push(finding)
      }
      groups.set(key, group)
    })
  })
  return [...groups.values()].map(group => {
    const sorted = [...group.findings].sort(
      (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity),
    )
    return { ...group, findings: sorted, severity: sorted[0].severity }
  })
}

/**
 * The open "!" once one of its findings is dismissed: the elements its other findings concern,
 * coloured by the worst of them, or null when it has none left.
 */
export const openMarkerAfterDismissal = (
  group: MarkerGroup,
  dismissed: SceneFinding,
): OpenFindingMarker | null => {
  const remaining = group.findings.filter(finding => !sameFinding(finding, dismissed))
  if (remaining.length === 0) return null
  return {
    key: group.key,
    elementGuids: group.elementGuids.filter(guid =>
      remaining.some(finding => finding.elementGuids.includes(guid)),
    ),
    // The findings are sorted worst first.
    severity: remaining[0].severity,
  }
}
